import {ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, Signal, untracked} from '@angular/core';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {filter, firstValueFrom, take} from 'rxjs';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {IStore} from '../../../../shared/shared-models/store/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IPriceBand} from '../../../../shared/shared-models/price-banding/price-band';
import {isStoreAdminForStore} from '../../../../shared/shared-utils/user-access/user-access.utils';
import {
  CollectionStoresSettingsService,
} from '../../../../shared/shared-services/firebase/collection-stores-settings.service';
import {
  copyAdvancedFilterGroups,
  copyAdvancedFilterGroupsFailure,
  copyAdvancedFilterGroupsSuccess,
} from '../../../core/store-shared-filter/shared-filter.actions';
import {IUserAccess} from '../../../user-access/models/user-access';
import {selectAllUserStoresOrdered, selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {
  selectUserAccess,
} from '../../../user-access/user-access-store/user-access.selectors';
import {
  COPYABLE_STORE_SETTING_LABELS,
  COPYABLE_STORE_SETTINGS,
  IAdvancedFilterGroup,
  ICopyStoreSettingsResult,
  IPriceBandingStoreSettings,
  TCopyableStoreSetting,
} from '../../models';
import {
  ADVANCED_FILTER_GRID_GENERAL,
  ADVANCED_FILTER_GRID_STOCK_TAKE,
  matchesAdvancedFilterGridCategory,
} from '../../models/general/advanced-filter-grid.constants';
import {IAdvancedFilterCopyResult} from '../../models/general/advanced-filter-copy-result.interface';
import {buildAdvancedFilterCopyResultLines} from '../../utils/advanced-filter-firestore.utils';
import {resolveAdvancedFilterIdsAfterDuplicateWarning} from '../../utils/advanced-filter-store-copy.utils';

interface IFilterCopyBetweenStoresOutcome {
  cancelled: boolean;
  fatalError: boolean;
  result: IAdvancedFilterCopyResult;
  categoryLabel?: string;
  emptySelection?: boolean;
  fatalMessage?: string;
}
import {settingStoreCopySettings, settingStorePriceBands} from '../../store/setting.actions';
import {
  selectCopyStoreSettingsResult,
  selectGlobalPriceBands,
  selectIsCopyingStoreSettings,
  selectIsPriceBandsLoading,
  selectPriceBandsByStore,
} from '../../store/setting.selectors';

/**
 * Settings page for copying store settings from one store to another.
 *
 * The user picks a source store, the settings to copy, and a target store.
 * Target stores are limited to the stores the user administers, because the
 * copy overwrites documents under `stores/{targetStoreId}/settings/**`.
 */
@Component({
  selector: 'app-feature-copy-settings',
  templateUrl: './feature-copy-settings.component.html',
  styleUrl: './feature-copy-settings.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureCopySettingsComponent implements OnInit {

  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _modalUtils = inject(SharedModalUtils);
  private readonly _storesSettings = inject(CollectionStoresSettingsService);

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;

  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _stores: Signal<IStore[]> = this._store.selectSignal(selectAllUserStoresOrdered);
  private readonly _userAccess: Signal<IUserAccess> = this._store.selectSignal(selectUserAccess);
  private readonly _priceBandsByStore: Signal<Record<string, IPriceBandingStoreSettings>> = this._store.selectSignal(
    selectPriceBandsByStore,
  );
  private readonly _globalPriceBands: Signal<IPriceBand[]> = this._store.selectSignal(selectGlobalPriceBands);
  private readonly _copyResult: Signal<ICopyStoreSettingsResult | null> = this._store.selectSignal(
    selectCopyStoreSettingsResult,
  );

  protected readonly isLoading: Signal<boolean> = this._store.selectSignal(selectIsPriceBandsLoading);
  protected readonly isCopyingPriceBanding: Signal<boolean> = this._store.selectSignal(selectIsCopyingStoreSettings);

  protected readonly sourceStoreId = signal<string>('');
  protected readonly targetStoreId = signal<string>('');
  protected readonly selectedSettings = signal<Set<TCopyableStoreSetting>>(new Set<TCopyableStoreSetting>(['priceBanding']));
  protected readonly sourceAdvancedFilters = signal<IAdvancedFilterGroup[]>([]);
  protected readonly isLoadingSourceFilters = signal<boolean>(false);
  protected readonly isCopyingFilters = signal<boolean>(false);
  private readonly isCopyRunInProgress = signal<boolean>(false);
  protected readonly selectedGeneralFilterIds = signal<Set<number>>(new Set<number>());
  protected readonly selectedStockTakeFilterIds = signal<Set<number>>(new Set<number>());

  private readonly handleCopyResultInComponent = signal<boolean>(false);
  private sourceFiltersRequestId = 0;

  private readonly adminStores = computed<IStore[]>(() => {
    const userAccess: IUserAccess = this._userAccess();
    return this._stores().filter((store: IStore): boolean =>
      isStoreAdminForStore(userAccess, store.storeId),
    );
  });

  protected readonly sourceStoreOptions = computed<Record<string, ISelectOption>>(() => {
    const selectedId: string = this.sourceStoreId();
    const options: Record<string, ISelectOption> = {};
    this._stores().forEach((store: IStore): void => {
      options[store.storeId] = {
        name: store.name,
        disabled: false,
        selected: store.storeId === selectedId,
      };
    });
    return options;
  });

  protected readonly targetStoreOptions = computed<Record<string, ISelectOption>>(() => {
    const selectedId: string = this.targetStoreId();
    const sourceId: string = this.sourceStoreId();
    const options: Record<string, ISelectOption> = {};
    this.adminStores()
      .filter((store: IStore): boolean => store.storeId !== sourceId)
      .forEach((store: IStore): void => {
        options[store.storeId] = {
          name: store.name,
          disabled: false,
          selected: store.storeId === selectedId,
        };
      });
    return options;
  });

  protected readonly settingOptions = computed<Record<string, ISelectOption>>(() => {
    const selected: Set<TCopyableStoreSetting> = this.selectedSettings();
    const options: Record<string, ISelectOption> = {};
    COPYABLE_STORE_SETTINGS.forEach((setting: TCopyableStoreSetting): void => {
      options[setting] = {
        name: COPYABLE_STORE_SETTING_LABELS[setting],
        disabled: false,
        selected: selected.has(setting),
      };
    });
    return options;
  });

  protected readonly generalFilterOptions = computed<Record<string, ISelectOption>>(() => {
    const selected = this.selectedGeneralFilterIds();
    const options: Record<string, ISelectOption> = {};
    this.sourceAdvancedFilters()
      .filter(
        (filter: IAdvancedFilterGroup): boolean =>
          filter.saved && matchesAdvancedFilterGridCategory(filter.grid, ADVANCED_FILTER_GRID_GENERAL),
      )
      .forEach((filter: IAdvancedFilterGroup): void => {
        const key: string = String(filter.id);
        options[key] = {
          name: filter.name?.trim() || `Filter ${filter.id}`,
          selected: selected.has(filter.id),
          disabled: false,
        };
      });
    return options;
  });

  protected readonly stockTakeFilterOptions = computed<Record<string, ISelectOption>>(() => {
    const selected = this.selectedStockTakeFilterIds();
    const options: Record<string, ISelectOption> = {};
    this.sourceAdvancedFilters()
      .filter(
        (filter: IAdvancedFilterGroup): boolean =>
          filter.saved && matchesAdvancedFilterGridCategory(filter.grid, ADVANCED_FILTER_GRID_STOCK_TAKE),
      )
      .forEach((filter: IAdvancedFilterGroup): void => {
        const key: string = String(filter.id);
        options[key] = {
          name: filter.name?.trim() || `Filter ${filter.id}`,
          selected: selected.has(filter.id),
          disabled: false,
        };
      });
    return options;
  });

  protected readonly sourceStoreName = computed<string>(() =>
    this._stores().find((store: IStore): boolean => store.storeId === this.sourceStoreId())?.name ?? '',
  );

  protected readonly isSourcePriceBandingLoaded = computed<boolean>(() => {
    const sourceId: string = this.sourceStoreId();
    return !!sourceId && !!this._priceBandsByStore()[sourceId];
  });

  protected readonly sourcePriceBandCount = computed<number>(() => {
    const sourceId: string = this.sourceStoreId();
    if (!sourceId) {
      return 0;
    }
    const storeBands: IPriceBand[] = this._priceBandsByStore()[sourceId]?.bands ?? [];
    return storeBands.length || this._globalPriceBands().length;
  });

  protected readonly isPriceBandingSelected = computed<boolean>(() => this.selectedSettings().has('priceBanding'));
  protected readonly isGeneralFiltersSelected = computed<boolean>(() => this.selectedSettings().has('generalFilters'));
  protected readonly isStockTakeFiltersSelected = computed<boolean>(() => this.selectedSettings().has('stockTakeFilters'));
  protected readonly hasGeneralFilters = computed<boolean>(() => Object.keys(this.generalFilterOptions()).length > 0);
  protected readonly hasStockTakeFilters = computed<boolean>(() => Object.keys(this.stockTakeFilterOptions()).length > 0);
  protected readonly hasTargetStores = computed<boolean>(() => Object.keys(this.targetStoreOptions()).length > 0);

  protected readonly isCopying = computed<boolean>(
    () => this.isCopyingPriceBanding() || this.isCopyingFilters() || this.isCopyRunInProgress(),
  );

  protected readonly canCopy = computed<boolean>(() => {
    if (!this.sourceStoreId() || !this.targetStoreId() || this.isCopying()) {
      return false;
    }
    const selected = this.selectedSettings();
    if (selected.size === 0) {
      return false;
    }
    const wantsGeneralFilters: boolean = selected.has('generalFilters');
    const wantsStockTakeFilters: boolean = selected.has('stockTakeFilters');
    if (wantsGeneralFilters || wantsStockTakeFilters) {
      if (this.isLoadingSourceFilters()) {
        return false;
      }
      if (wantsGeneralFilters && this.selectedGeneralFilterIds().size === 0) {
        return false;
      }
      if (wantsStockTakeFilters && this.selectedStockTakeFilterIds().size === 0) {
        return false;
      }
    }
    return selected.has('priceBanding') || wantsGeneralFilters || wantsStockTakeFilters;
  });

  constructor() {
    effect(() => {
      const storeId: string = this._selectedStore()?.storeId ?? '';
      untracked(() => {
        if (!storeId || this.sourceStoreId()) {
          return;
        }
        this.sourceStoreId.set(storeId);
        const store: IStore | undefined = this._stores().find((entry: IStore): boolean => entry.storeId === storeId);
        if (store) {
          this.loadSourceAdvancedFilters(store);
        }
      });
    });

    effect(() => {
      const result: ICopyStoreSettingsResult | null = this._copyResult();
      untracked(() => {
        if (!result || this.handleCopyResultInComponent()) {
          return;
        }
        void this.showPriceBandingResultModal(result);
      });
    });
  }

  ngOnInit(): void {
    this._store.dispatch(settingStorePriceBands.getPriceBands({
      params: {storeAction: 'All'},
    }));
  }

  protected onSourceStoreChange(selection: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selection).find((option: string) => selection[option].selected);
    this.sourceStoreId.set(key ?? '');
    if (key && this.targetStoreId() === key) {
      this.targetStoreId.set('');
    }
    const store: IStore | undefined = this._stores().find((entry: IStore): boolean => entry.storeId === key);
    if (store) {
      this.loadSourceAdvancedFilters(store);
    } else {
      this.sourceFiltersRequestId++;
      this.isLoadingSourceFilters.set(false);
      this.sourceAdvancedFilters.set([]);
      this.selectedGeneralFilterIds.set(new Set<number>());
      this.selectedStockTakeFilterIds.set(new Set<number>());
    }
  }

  protected onTargetStoreChange(selection: Record<string, ISelectOption>): void {
    const key: string | undefined = Object.keys(selection).find((option: string) => selection[option].selected);
    this.targetStoreId.set(key ?? '');
  }

  protected onSettingsChange(selection: Record<string, ISelectOption>): void {
    const settings = new Set<TCopyableStoreSetting>();
    COPYABLE_STORE_SETTINGS.forEach((setting: TCopyableStoreSetting): void => {
      if (selection[setting]?.selected) {
        settings.add(setting);
      }
    });
    this.selectedSettings.set(settings);
    if (!settings.has('generalFilters')) {
      this.selectedGeneralFilterIds.set(new Set<number>());
    }
    if (!settings.has('stockTakeFilters')) {
      this.selectedStockTakeFilterIds.set(new Set<number>());
    }
  }

  protected onGeneralFiltersSelectionChange(selection: Record<string, ISelectOption>): void {
    const ids = new Set<number>();
    Object.entries(selection).forEach(([key, option]: [string, ISelectOption]): void => {
      if (option.selected) {
        const id: number = Number(key);
        if (!Number.isNaN(id)) {
          ids.add(id);
        }
      }
    });
    this.selectedGeneralFilterIds.set(ids);
  }

  protected onStockTakeFiltersSelectionChange(selection: Record<string, ISelectOption>): void {
    const ids = new Set<number>();
    Object.entries(selection).forEach(([key, option]: [string, ISelectOption]): void => {
      if (option.selected) {
        const id: number = Number(key);
        if (!Number.isNaN(id)) {
          ids.add(id);
        }
      }
    });
    this.selectedStockTakeFilterIds.set(ids);
  }

  protected async onCopyClick(): Promise<void> {
    if (!this.canCopy()) {
      return;
    }

    const sourceStore: IStore | undefined = this._stores().find(
      (store: IStore): boolean => store.storeId === this.sourceStoreId(),
    );
    const targetStore: IStore | undefined = this._stores().find(
      (store: IStore): boolean => store.storeId === this.targetStoreId(),
    );
    if (!sourceStore || !targetStore) {
      return;
    }

    const selected = this.selectedSettings();
    const includesFilters: boolean = selected.has('generalFilters') || selected.has('stockTakeFilters');
    const settingLabels: string = Array.from(selected)
      .map((setting: TCopyableStoreSetting): string => COPYABLE_STORE_SETTING_LABELS[setting])
      .join(', ');

    const confirmation: IModalDismissResult = await this._modalUtils.createModal({
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: 'Confirm copy',
      contextDescription: [
        `${settingLabels} will be copied from "${sourceStore.name}" to "${targetStore.name}".`,
        ...(selected.has('priceBanding')
          ? [`Existing price banding on "${targetStore.name}" will be replaced.`]
          : []),
        ...(includesFilters
          ? ['You may be asked how to handle duplicate filter names on the target store.']
          : []),
      ],
      buttonAcceptText: 'Copy',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });

    if (confirmation.data?.buttonPressed !== 'button-accept') {
      return;
    }

    if (!this.canCopy()) {
      return;
    }

    this.isCopyRunInProgress.set(true);
    this.handleCopyResultInComponent.set(true);

    const summaryLines: string[] = [];
    let anyFailure = false;

    try {
      if (selected.has('priceBanding')) {
        const priceBandingOk: boolean = await this.copyPriceBanding(sourceStore, targetStore);
        if (priceBandingOk) {
          summaryLines.push(`Price banding copied to "${targetStore.name}".`);
        } else {
          anyFailure = true;
          summaryLines.push(`Price banding could not be copied to "${targetStore.name}".`);
        }
      }

      let filterCopyCancelled = false;

      if (selected.has('generalFilters')) {
        const generalOutcome = await this.copyFiltersBetweenStores(
          sourceStore,
          targetStore,
          ADVANCED_FILTER_GRID_GENERAL,
          'General Filters',
          Array.from(this.selectedGeneralFilterIds()),
        );
        this.appendFilterCopySummary(
          summaryLines,
          (failed: boolean): void => {
            if (failed) {
              anyFailure = true;
            }
          },
          generalOutcome,
          targetStore.name,
        );
        filterCopyCancelled = generalOutcome.cancelled;
      }

      if (selected.has('stockTakeFilters') && !filterCopyCancelled) {
        this.appendFilterCopySummary(
          summaryLines,
          (failed: boolean): void => {
            if (failed) {
              anyFailure = true;
            }
          },
          await this.copyFiltersBetweenStores(
            sourceStore,
            targetStore,
            ADVANCED_FILTER_GRID_STOCK_TAKE,
            'Stock Take Filters',
            Array.from(this.selectedStockTakeFilterIds()),
          ),
          targetStore.name,
        );
      } else if (selected.has('stockTakeFilters') && filterCopyCancelled) {
        summaryLines.push('Stock take filter copy was not started because the previous filter copy was cancelled.');
      }
    } finally {
      this.isCopyRunInProgress.set(false);
      this.handleCopyResultInComponent.set(false);
    }

    if (summaryLines.length > 0) {
      await this._modalUtils.createModal({
        modalTitleIcon: anyFailure ? IconsEnum.alertBox : IconsEnum.checkCircleOutline,
        modalTitleIconColor: anyFailure ? IconColourEnum.red : IconColourEnum.green,
        modalTitle: anyFailure ? 'Copy completed with errors' : 'Copy complete',
        contextDescription: summaryLines,
        buttonAcceptText: '',
        buttonCloseText: 'OK',
        buttonAccept: false,
        buttonClose: true,
        buttonCloseCross: false,
        cssClass: ['shared-basic-modal-css-small'],
      });
      if (includesFilters) {
        this.targetStoreId.set('');
        this.selectedGeneralFilterIds.set(new Set<number>());
        this.selectedStockTakeFilterIds.set(new Set<number>());
      }
    }
  }

  private loadSourceAdvancedFilters(store: IStore): void {
    const requestId: number = ++this.sourceFiltersRequestId;
    this.isLoadingSourceFilters.set(true);
    this.sourceAdvancedFilters.set([]);
    this.selectedGeneralFilterIds.set(new Set<number>());
    this.selectedStockTakeFilterIds.set(new Set<number>());
    void this._storesSettings
      .getStoreSettingsAdvancedFilterGroups(store)
      .then((filters: IAdvancedFilterGroup[]): void => {
        if (requestId !== this.sourceFiltersRequestId) {
          return;
        }
        this.sourceAdvancedFilters.set(filters);
      })
      .catch((): void => {
        if (requestId !== this.sourceFiltersRequestId) {
          return;
        }
        this.sourceAdvancedFilters.set([]);
      })
      .finally((): void => {
        if (requestId !== this.sourceFiltersRequestId) {
          return;
        }
        this.isLoadingSourceFilters.set(false);
      });
  }

  private async copyPriceBanding(sourceStore: IStore, targetStore: IStore): Promise<boolean> {
    const completion$ = this._actions$.pipe(
      ofType(settingStoreCopySettings.copyStoreSettingsSuccess, settingStoreCopySettings.copyStoreSettingsFailure),
      take(1),
    );
    this._store.dispatch(settingStoreCopySettings.copyStoreSettings({
      settings: ['priceBanding'],
      sourceStore,
      targetStore,
    }));
    const finalAction = await firstValueFrom(completion$);
    this._store.dispatch(settingStoreCopySettings.clearCopyStoreSettingsResult());
    return finalAction.type === settingStoreCopySettings.copyStoreSettingsSuccess.type;
  }

  private appendFilterCopySummary(
    summaryLines: string[],
    setFailed: (failed: boolean) => void,
    outcome: IFilterCopyBetweenStoresOutcome,
    targetStoreName: string,
  ): void {
    if (outcome.emptySelection) {
      summaryLines.push(
        outcome.categoryLabel
          ? `No ${outcome.categoryLabel} were selected to copy.`
          : 'No filters were selected to copy.',
      );
      setFailed(true);
      return;
    }
    if (outcome.cancelled) {
      summaryLines.push('Filter copy was cancelled.');
      setFailed(true);
      return;
    }
    if (outcome.fatalError) {
      setFailed(true);
      summaryLines.push(
        outcome.fatalMessage
          ? `Filters could not be copied to "${targetStoreName}": ${outcome.fatalMessage}`
          : `Filters could not be copied to "${targetStoreName}".`,
      );
      return;
    }
    const lines: string[] = buildAdvancedFilterCopyResultLines(outcome.result, {
      copiedLine: (count: number): string =>
        `${count} filter${count === 1 ? '' : 's'} copied to "${targetStoreName}".`,
      skippedLine: (count: number): string =>
        `${count} filter${count === 1 ? '' : 's'} already existed on "${targetStoreName}" and were not copied again.`,
      failureHeader: (count: number): string =>
        `${count} filter${count === 1 ? '' : 's'} could not be copied:`,
    });
    if (outcome.result.failures.length > 0) {
      setFailed(true);
    }
    summaryLines.push(...lines);
  }

  /**
   * Returns copy outcome, or cancelled when the user dismisses duplicate-name handling.
   */
  private async copyFiltersBetweenStores(
    sourceStore: IStore,
    targetStore: IStore,
    category: typeof ADVANCED_FILTER_GRID_GENERAL | typeof ADVANCED_FILTER_GRID_STOCK_TAKE,
    categoryLabel: string,
    filterIds: number[],
  ): Promise<IFilterCopyBetweenStoresOutcome> {
    const emptyResult: IAdvancedFilterCopyResult = {copiedCount: 0, skippedCount: 0, failures: []};
    if (filterIds.length === 0) {
      return {
        cancelled: false,
        fatalError: false,
        emptySelection: true,
        categoryLabel,
        result: emptyResult,
      };
    }

    this.isCopyingFilters.set(true);
    try {
      const selectedFilters: IAdvancedFilterGroup[] = this.sourceAdvancedFilters().filter(
        (filter: IAdvancedFilterGroup): boolean =>
          filterIds.includes(filter.id) && matchesAdvancedFilterGridCategory(filter.grid, category),
      );

      const resolvedIds: number[] | null = await resolveAdvancedFilterIdsAfterDuplicateWarning(
        this._modalUtils,
        this._storesSettings,
        selectedFilters,
        targetStore,
        category,
        categoryLabel,
      );
      if (resolvedIds === null) {
        return {cancelled: true, fatalError: false, result: emptyResult};
      }
      if (resolvedIds.length === 0) {
        return {
          cancelled: false,
          fatalError: false,
          result: {
            copiedCount: 0,
            skippedCount: selectedFilters.length,
            failures: [],
          },
        };
      }

      const completion$ = this._actions$.pipe(
        ofType(copyAdvancedFilterGroupsSuccess, copyAdvancedFilterGroupsFailure),
        filter(
          (
            action:
              | ReturnType<typeof copyAdvancedFilterGroupsSuccess>
              | ReturnType<typeof copyAdvancedFilterGroupsFailure>,
          ): boolean => {
            if (action.type === copyAdvancedFilterGroupsFailure.type) {
              return true;
            }
            return action.targetStore.storeId === targetStore.storeId;
          },
        ),
        take(1),
      );
      this._store.dispatch(copyAdvancedFilterGroups({
        sourceStore,
        targetStore,
        filterIds: resolvedIds,
      }));

      const finalAction = await firstValueFrom(completion$);
      if (finalAction.type === copyAdvancedFilterGroupsFailure.type) {
        return {
          cancelled: false,
          fatalError: true,
          fatalMessage: finalAction.error?.message,
          result: emptyResult,
        };
      }
      return {
        cancelled: false,
        fatalError: false,
        result: {
          copiedCount: finalAction.copiedCount,
          skippedCount: finalAction.skippedCount,
          failures: finalAction.failures,
        },
      };
    } finally {
      this.isCopyingFilters.set(false);
    }
  }

  private async showPriceBandingResultModal(result: ICopyStoreSettingsResult): Promise<void> {
    const settingLabels: string = result.settings
      .map((setting: TCopyableStoreSetting): string => COPYABLE_STORE_SETTING_LABELS[setting])
      .join(', ');

    await this._modalUtils.createModal({
      modalTitleIcon: result.succeeded ? IconsEnum.checkCircleOutline : IconsEnum.alertBox,
      modalTitleIconColor: result.succeeded ? IconColourEnum.green : IconColourEnum.red,
      modalTitle: result.succeeded ? 'Settings copied' : 'Copy failed',
      contextDescription: [
        result.succeeded
          ? `${settingLabels} copied to "${result.targetStoreName}".`
          : `${settingLabels} could not be copied to "${result.targetStoreName}". Please try again.`,
      ],
      buttonAcceptText: '',
      buttonCloseText: 'OK',
      buttonAccept: false,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });

    this._store.dispatch(settingStoreCopySettings.clearCopyStoreSettingsResult());
    if (result.succeeded) {
      this.targetStoreId.set('');
    }
  }

}
